// Shared types, formatting, and list helpers for the integrated Clockora UI.
// Ported from clockora/src/lib/{types,format}.ts (frontend-safe subset).

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
export const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ---------- government ID formatting ----------

const GOV_PATTERNS = {
  tin: [3, 3, 3, 3], // 000-000-000-000
  sss: [2, 7, 1], // 00-0000000-0
  philhealth: [2, 9, 1], // 00-000000000-0
  hdmf: [4, 4, 4], // 0000-0000-0000
};

export const GOV_FIELDS = [
  { kind: "tin", label: "TIN Number", placeholder: "000-000-000-000" },
  { kind: "sss", label: "SSS Number", placeholder: "00-0000000-0" },
  { kind: "philhealth", label: "PhilHealth Number", placeholder: "00-000000000-0" },
  { kind: "hdmf", label: "HDMF / Pag-IBIG Number", placeholder: "0000-0000-0000" },
];

/** Keep digits and dashes, rebuild canonical dash groups from the digits. */
export function formatGovId(kind, raw) {
  const segs = GOV_PATTERNS[kind];
  const maxDigits = segs.reduce((a, b) => a + b, 0);
  const digits = String(raw || "").replace(/[^0-9]/g, "").slice(0, maxDigits);
  let out = "";
  let i = 0;
  for (let s = 0; s < segs.length && i < digits.length; s++) {
    out += (s > 0 ? "-" : "") + digits.slice(i, i + segs[s]);
    i += segs[s];
  }
  return out;
}

// ---------- name / avatar ----------

export function initials(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

/** Deterministic hue from a string (for avatar gradients). */
export function hueOf(seed) {
  let h = 0;
  const s = String(seed || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

// ---------- time (always rendered in UTC+8 — Asia/Manila) ----------

export const DISPLAY_TZ = "Asia/Manila";

function asDate(iso) {
  return typeof iso === "string" ? new Date(iso) : iso;
}

/** Captured timestamps rendered as `hh:mm AM/PM` in UTC+8. */
export function fmtTime(iso) {
  return asDate(iso).toLocaleTimeString("en-US", {
    timeZone: DISPLAY_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function fmtDate(iso) {
  return asDate(iso).toLocaleDateString("en-US", {
    timeZone: DISPLAY_TZ,
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function fmtDateLong(d) {
  return asDate(d).toLocaleDateString("en-US", {
    timeZone: DISPLAY_TZ,
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function relAgo(iso, nowMs) {
  const then = new Date(iso).getTime();
  const diff = Math.max(0, (nowMs ?? Date.now()) - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function fmtDur(mins) {
  const m = Math.round(mins);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/** Convert "HH:MM" to 12-hour label like "8:00 AM". */
export function hm12(hm) {
  const [h, m] = String(hm).split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${suffix}`;
}

// ---------- roster ordering ----------

/**
 * Canonical roster order: active accounts first, inactive last,
 * each group sorted alphabetically by full name.
 */
export function sortRoster(list) {
  return [...list].sort((a, b) => {
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    const nameCmp = String(a.fullName).localeCompare(String(b.fullName), "en", { sensitivity: "base" });
    if (nameCmp !== 0) return nameCmp;
    const codeCmp = String(a.code || "").localeCompare(String(b.code || ""), "en", { sensitivity: "base" });
    if (codeCmp !== 0) return codeCmp;
    return Number(a.id || 0) - Number(b.id || 0);
  });
}

// ---------- date helpers (always in DISPLAY_TZ) ----------

/** Today in DISPLAY_TZ formatted as YYYY-MM-DD (for HTML date inputs). */
export function todayYmd() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: DISPLAY_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value ?? "0000";
  const m = parts.find((p) => p.type === "month")?.value ?? "00";
  const d = parts.find((p) => p.type === "day")?.value ?? "00";
  return `${y}-${m}-${d}`;
}

/** Strict YYYY-MM-DD validator (numeric ranges only; does not validate calendar). */
export function isValidYmd(s) {
  if (typeof s !== "string" || s.length !== 10) return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const [, yy, mm, dd] = m.map(Number);
  return mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31 && yy >= 1970 && yy <= 9999;
}

/** Convert any ISO/Date-parseable input to YYYY-MM-DD in DISPLAY_TZ (HTML date input value). */
export function toYmd(iso) {
  if (!iso) return todayYmd();
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: DISPLAY_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(asDate(iso));
    const y = parts.find((p) => p.type === "year")?.value ?? "0000";
    const m = parts.find((p) => p.type === "month")?.value ?? "00";
    const d = parts.find((p) => p.type === "day")?.value ?? "00";
    return `${y}-${m}-${d}`;
  } catch {
    return todayYmd();
  }
}

/** Convert any ISO/Date-parseable input to HH:MM (24h) in DISPLAY_TZ (HTML time input value). */
export function toTimeHm(iso) {
  if (!iso) return "00:00";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: DISPLAY_TZ,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(asDate(iso));
    const h = parts.find((p) => p.type === "hour")?.value ?? "00";
    const m = parts.find((p) => p.type === "minute")?.value ?? "00";
    const hh = /^\d+$/.test(h) ? String(Math.max(0, Math.min(23, Number(h)))).padStart(2, "0") : h;
    return `${hh}:${m}`;
  } catch {
    return "00:00";
  }
}
